import React, { useEffect, useMemo, useState } from "react";
import { FirebaseUser } from "../types/user";
import { listAdminUsers, getAdminUser } from "../services/adminService";
import {
  compareUserValues,
  formatUserDate,
  isToday,
} from "../utils/user-directory";
import { specialties, specialtyName } from "../data/specialties";
import SelectedUserManagementModal from "../components/SelectedUserManagementModal";
import EmailUsersModal from "../components/EmailUsersModal";
import "./stylesheets/UserManagement.css";

const sortOptions = [
  ["createdAt", "Signup date"],
  ["lastLoginAt", "Last login"],
  ["name", "Name"],
  ["email", "Email"],
  ["uid", "User ID"],
  ["unlockedSpecialties", "Specialties"],
];
const sortPresets = [
  { key: "createdAt", direction: "desc", label: "Newest signups" },
  { key: "createdAt", direction: "asc", label: "Oldest signups" },
  { key: "lastLoginAt", direction: "desc", label: "Recently logged in" },
  { key: "name", direction: "asc", label: "Name A–Z" },
  { key: "name", direction: "desc", label: "Name Z–A" },
  { key: "email", direction: "asc", label: "Email A–Z" },
] as const;
export default function UserManagement() {
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: "createdAt",
    direction: "desc",
  });
  const [selectedUser, setSelectedUser] = useState<FirebaseUser | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setUsers(await listAdminUsers());
    } catch (e: any) {
      setError("Unable to load the user directory. " +
        (e.code === "functions/permission-denied" || e.code === "functions/unauthenticated"
          ? "Sign out and sign back in to refresh your administrator access."
          : "Check that the administrator Firebase functions are deployed for this environment, then retry."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const refreshSelectedUser = async () => {
    if (!selectedUser) return;
    try {
      const updated = await getAdminUser(selectedUser.uid);
      setSelectedUser(updated);
      setUsers((items) =>
        updated
          ? items.map((u) => (u.uid === updated.uid ? updated : u))
          : items.filter((u) => u.uid !== selectedUser.uid),
      );
    } catch (e: any) {
      setError(e.message || "Unable to refresh user.");
    }
  };
  const results = useMemo(
    () =>
      users
        .filter((user) => {
          if (filter === "specialties" && !user.unlockedSpecialties?.length)
            return false;
          if (filter === "loginToday" && !isToday(user.lastLoginAt))
            return false;
          if (filter === "createdToday" && !isToday(user.createdAt))
            return false;
          if (
            specialtyFilter &&
            !user.unlockedSpecialties?.includes(specialtyFilter)
          )
            return false;
          return [
            user.name,
            user.email,
            user.uid,
            ...(user.unlockedSpecialties || []).map(specialtyName),
          ].some((value) =>
            String(value || "")
              .toLowerCase()
              .includes(search.trim().toLowerCase()),
          );
        })
        .sort((a, b) =>
          compareUserValues(
            (a as any)[sort.key],
            (b as any)[sort.key],
            sort.key,
            sort.direction,
          ),
        ),
    [users, search, sort, filter, specialtyFilter],
  );
  const chooseSort = (key: string) =>
    setSort((current) => ({
      key,
      direction:
        current.key === key
          ? current.direction === "asc"
            ? "desc"
            : "asc"
          : key.endsWith("At")
            ? "desc"
            : "asc",
    }));
  return (
    <div className="operation-content user-directory">
      <div className="directory-heading"><div><p className="directory-eyebrow">MockMedic Manager</p><h2>Users</h2></div>
        <button className="directory-refresh" onClick={() => void load()} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
      </div>
      <div className="analytics-grid">
        {[
          ["all", "All users", users.length],
          [
            "specialties",
            "With specialties",
            users.filter((u) => u.unlockedSpecialties?.length).length,
          ],
          [
            "loginToday",
            "Active today",
            users.filter((u) => isToday(u.lastLoginAt)).length,
          ],
          [
            "createdToday",
            "New today",
            users.filter((u) => isToday(u.createdAt)).length,
          ],
        ].map(([value, label, count]) => (
          <button
            key={value}
            type="button"
            className={`analytics-card ${filter === value ? "active" : ""}`}
            aria-pressed={filter === value}
            onClick={() => setFilter(String(value))}
          >
            <h3>{label}</h3>
            <span className="analytics-number">{loading || error ? "—" : count}</span>
          </button>
        ))}
      </div>
      <div className="directory-search">
        <label>
          Search users
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or specialty"
          />
        </label>
      </div>
      <div className="directory-controls">
        <label>
          Specialty
          <select
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
          >
            <option value="">All specialties</option>
            {specialties.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Order
          <select value={`${sort.key}-${sort.direction}`} onChange={(e) => {
            const preset = sortPresets.find(p => `${p.key}-${p.direction}` === e.target.value);
            if (preset) setSort({ key: preset.key, direction: preset.direction });
          }}>
            {sortPresets.map(preset => <option key={`${preset.key}-${preset.direction}`} value={`${preset.key}-${preset.direction}`}>{preset.label}</option>)}
            {!sortPresets.some(p => p.key === sort.key && p.direction === sort.direction) && <option value={`${sort.key}-${sort.direction}`}>{sortOptions.find(([key]) => key === sort.key)?.[1]} {sort.direction === "asc" ? "↑" : "↓"}</option>}
          </select>
        </label>
      </div>
      {(search || specialtyFilter || filter !== "all") && <div className="directory-active-filters"><span>{filter === "specialties" ? "With specialties" : filter === "loginToday" ? "Logged in today" : filter === "createdToday" ? "Signed up today" : "Filtered users"}</span><button
          onClick={() => {
            setSearch("");
            setSpecialtyFilter("");
            setFilter("all");
          }}
        >
          Reset filters ×
        </button></div>}
      {error && <div className="directory-error" role="alert"><h3>Users couldn’t be loaded</h3><p>{error}</p><button onClick={() => void load()}>Try again</button></div>}
      {loading ? (
        <p role="status">Loading users…</p>
      ) : !error && (
        <div className="users-table-wrapper">
          <div className="directory-results-heading"><strong>{results.length} {results.length === 1 ? "user" : "users"}</strong><details className="directory-more-actions"><summary>Actions</summary><button onClick={() => setEmailModalOpen(true)} disabled={!results.length}>Email these users</button></details></div>
          <div className="directory-user-cards">
            {results.length === 0 && <p className="directory-empty">No users match these filters. Try clearing the filters.</p>}
            {results.map((u) => <button className="directory-user-card" key={u.uid} onClick={() => setSelectedUser(u)}>
              <div className="directory-card-identity"><span className="directory-avatar" aria-hidden="true">{(u.name || u.email || "?").slice(0, 1).toUpperCase()}</span><div><strong>{u.name || "Unnamed user"}</strong><span>{u.email || "No email recorded"}</span></div><span aria-hidden="true">›</span></div>
              <span className={`directory-account-status ${u.disabled ? "is-disabled" : !u.emailVerified ? "is-unverified" : ""}`}>{u.disabled ? "Disabled" : u.emailVerified ? "Email verified" : "Email unverified"}</span>
              <dl className="directory-card-dates"><div><dt>Signed up · UK</dt><dd>{formatUserDate(u.createdAt)}</dd></div><div><dt>Last login · UK</dt><dd>{formatUserDate(u.lastLoginAt)}</dd></div></dl>
              <div className="directory-specialties">{u.unlockedSpecialties?.length ? u.unlockedSpecialties.map(s => <span key={s}>{specialtyName(s)}</span>) : <span>No specialties</span>}</div>
              <span className="directory-card-link">Manage user <span aria-hidden="true">→</span></span>
            </button>)}
          </div>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  {sortOptions.map(([key, label]) => (
                    <th
                      key={key}
                      aria-sort={
                        sort.key === key
                          ? sort.direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <button
                        className="directory-sort"
                        onClick={() => chooseSort(key)}
                      >
                        {label}
                        {sort.key === key
                          ? sort.direction === "asc"
                            ? " ↑"
                            : " ↓"
                          : ""}
                      </button>
                    </th>
                  ))}
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {results.length === 0 && (
                  <tr>
                    <td colSpan={7}>No users match your filters.</td>
                  </tr>
                )}
                {results.map((u) => (
                  <tr
                    key={u.uid}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedUser(u)}
                  >
                    <td data-label="Signup date">
                      {formatUserDate(u.createdAt)}
                    </td>
                    <td data-label="Last login">
                      {formatUserDate(u.lastLoginAt)}
                    </td>
                    <td data-label="Name">
                      <button
                        className="directory-open"
                        onClick={() => setSelectedUser(u)}
                      >
                        {u.name || "Manage user"}
                      </button>
                    </td>
                    <td data-label="Email">{u.email}</td>
                    <td data-label="User ID" className="directory-uid">
                      {u.uid}
                    </td>
                    <td data-label="Specialties">
                      {u.unlockedSpecialties?.map(specialtyName).join(", ") ||
                        "None"}
                    </td>
                    <td data-label="Account">
                      {u.disabled
                        ? "Disabled"
                        : u.emailVerified
                          ? "Email verified"
                          : "Email unverified"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selectedUser && (
        <SelectedUserManagementModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUserUpdate={() => void refreshSelectedUser()}
        />
      )}
      {emailModalOpen && (
        <EmailUsersModal
          users={results}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
}
